import React from 'react'
import { getCookie } from '../../../../../utils/cookies'
import { datalayerHandler } from '../../../../../utils/datalayer'
import { Image } from '../../../../../utils/image'
import { Link } from '../../../../../utils/link'
import { ctaBlockClasses, expandBlockClasses } from '../../../../../utils/style'
import { convertReactNodeToString, getClassName, htmlDecode, slugify } from '../../../../../utils/utils'
import { useInView } from '../../../../hooks/useInView'
import { ExpandBlock } from '../../blocksState'
import { LinkBlockNestedGridLayout, LinkBlockNestedLayout } from './nestedBlocks/linkBlockNestedLayout'
import { MediaBlockNestedLayout } from './nestedBlocks/mediaBlockNestedLayout'
import { QuoteBlockNestedLayout } from './nestedBlocks/quoteBlockNestedLayout'
import { TextBlockNestedLayout } from './nestedBlocks/textBlockNestedLayout'

export type ExpandBlockStyle = Partial<{
  blockClassName: string
  inviewClassName: string
  innerClassName: string
  headerClassName: string
  titleClassName: string
  introClassName: string
  contentClassName: string
  listClassName: string
  footerClassName: string
  clickableClassName: string
  clickableWrapperClassName: string
  clickableLabelClassName: string
  clickableIconClassName: string
  expandItemClassName: string
  expandButtonClassName: string
  expandTitleClassName: string
  expandSubtitleClassName: string
  expandIconClassName: string
  expandContentClassName: string
  expandTextClassName: string
  iconId: string
  iconOpenId: string
  iconClosedId: string
}>

export const ExpandBlockLayout = (
  props: ExpandBlock & { expand: (_: number) => void } & {
    acceptVideoCookie: () => void
  }
) => {
  const { inview, index, intro, primaryAction, title, image, displayGrid, items, expandBlockState, expand, acceptVideoCookie, } = props
  const classes = ctaBlockClasses(props.image !== undefined)

  const blockClasses = expandBlockClasses(image != undefined, displayGrid)
  const videoCookieAccepted = getCookie("video-cookies") == 'yes'
  const { inView, ref } = useInView(
    { threshold: 0.1 },
    (inview) => console.debug("ExpandBlockLayout inView:", inview),
    !inview?.triggerOnce
  )

  return (
    <section
      key={`block-${index}`}
      id={`io-${index}`}
      ref={ref}
      className={getClassName(blockClasses, 'blockClassName', 'container') + ' ' + (inView ? getClassName(blockClasses, 'inviewClassName', 'inview') : '')}
    >
      <div className={getClassName(blockClasses, 'innerClassName', 'py-5')}>
        {(title || intro) && (
          <header className={getClassName(blockClasses, 'headerClassName', '')}>
            {title && (
              <h2 className={getClassName(blockClasses, 'titleClassName', 'mb-3')}>
                <span id={`${index}`} aria-hidden="true" className="block__anchor" />
                {title}
              </h2>
            )}
            {intro && <p className={getClassName(blockClasses, 'introClassName', '')}>{intro}</p>}
          </header>
        )}

        {items && items.count() > 0 && (
          <div className={getClassName(blockClasses, 'contentClassName', '')}>
            {image && (
              <div className={'block__visual'}>
                <Image
                  {...image}
                  classNames={{
                    image: getClassName(classes, 'imageClassName'),
                    picture: getClassName(classes, 'pictureClassName', ''),
                  }}
                />
              </div>
            )}
            <ul className={getClassName(blockClasses, 'listClassName', 'accordion list-unstyled mb-3')}>
              {items.toArray().map((item, i) => {
                const expanded = expandBlockState.get(i)
                const { title, subtitle, SVGIcon, text, nestedBlocks } = item
                return (
                  <li
                    key={`expand-${index}-${slugify(item.title)}`}
                    className={getClassName(blockClasses, 'expandItemClassName', 'card')}
                  >
                    {!displayGrid ? (
                      <button
                        className={getClassName(
                          blockClasses,
                          'expandButtonClassName',
                          `card-header ${expanded ? 'col--open' : 'col--closed'} text-left border-0`
                        )}
                        aria-controls={`a11y-${index}-${index}`}
                        aria-expanded={expanded}
                        onClick={() => {
                          expanded ||
                            datalayerHandler('expand', title, 'Expandable', {
                              kind: 'button',
                              title: convertReactNodeToString(title),
                              label: title,
                            })
                          expand(i)
                        }}
                      >
                        {title && (
                          <span className={getClassName(blockClasses, 'expandTitleClassName', 'btn btn-link m-0 p-0')}>
                            {title}
                          </span>
                        )}
                        {subtitle && (
                          <span className={getClassName(blockClasses, 'expandSubtitleClassName', 'text-muted m-0')}>
                            {subtitle}
                          </span>
                        )}
                        {SVGIcon && (
                          <svg
                            className={getClassName(blockClasses, 'expandIconClassName', 'mr-3')}
                            aria-hidden="true"
                            width={SVGIcon.width ? SVGIcon.width : '24px'}
                            height={SVGIcon.height ? SVGIcon.height : '24px'}
                          >
                            <use
                              xlinkHref={`${expanded
                                  ? getClassName(blockClasses, 'iconOpenId', '#icon--chevron-up')
                                  : getClassName(blockClasses, 'iconClosedId', '#icon--chevron-down')
                                }`}
                            />
                          </svg>
                        )}
                      </button>
                    ) : (
                      <div>
                        {title && (
                          <p className={getClassName(blockClasses, 'expandTitleClassName', 'btn btn-link m-0 p-0')}>
                            {title}
                          </p>
                        )}
                        {subtitle && (
                          <p className={getClassName(blockClasses, 'expandSubtitleClassName', 'text-muted m-0')}>
                            {subtitle}
                          </p>
                        )}
                      </div>
                    )}

                    <div
                      id={`a11y-${index}-${index}`}
                      className={getClassName(
                        blockClasses,
                        'expandContentClassName',
                        `card-body pb-1 ${expanded ? '' : 'd-none'} text-left border-0`
                      )}
                      aria-hidden={!expanded}
                    >
                      {text && (
                        <div
                          className={getClassName(blockClasses, 'expandTextClassName', '')}
                          dangerouslySetInnerHTML={{ __html: text }}
                        />
                      )}

                      {nestedBlocks &&
                        nestedBlocks.size > 0 &&
                        nestedBlocks.map((block, index) => {
                          switch (block.kind) {
                            case 'textBlockNested':
                              return <TextBlockNestedLayout {...block} key={index} />
                            case 'linkBlockNested':
                              return displayGrid ? <LinkBlockNestedGridLayout {...block} key={index} /> : <LinkBlockNestedLayout {...block} key={index} />
                            case 'mediaBlockNested':
                              return (
                                <MediaBlockNestedLayout {...block} videoCookieAccepted={videoCookieAccepted} acceptVideoCookie={acceptVideoCookie} key={index} />
                              )
                            case 'quoteBlockNested':
                              return <QuoteBlockNestedLayout {...block} key={index} />
                          }
                        })}
                    </div>
                  </li>
                )
              })}
            </ul>
          </div>
        )}

        {primaryAction && primaryAction.title && (
          <footer className={getClassName(blockClasses, 'footerClassName', '')}>
            <div className={getClassName(blockClasses, 'clickableWrapperClassName', 'd-inline-block')}>
              <Link
                url={primaryAction.url}
                title={primaryAction.title}
                className={getClassName(blockClasses, 'clickableClassName', 'btn btn-dark d-inline-flex')}
                additionalOnClick={() =>
                  datalayerHandler('expand', undefined, undefined, {
                    kind: 'link',
                    title: convertReactNodeToString(title),
                    link: primaryAction.url,
                    label: primaryAction.title,
                  })
                }
              >
                <span className={getClassName(blockClasses, 'clickableLabelClassName', '')}>
                  {htmlDecode(primaryAction.title)}
                </span>
                {primaryAction.SVGIcon && (
                  <svg
                    className={getClassName(blockClasses, 'clickableIconClassName', 'ml-3')}
                    aria-hidden="true"
                    width={primaryAction.SVGIcon.width ? primaryAction.SVGIcon.width : '24px'}
                    height={primaryAction.SVGIcon.height ? primaryAction.SVGIcon.height : '24px'}
                  >
                    <use xlinkHref={getClassName(blockClasses, 'iconId', '#icon--chevron-right-large')} />
                  </svg>
                )}
              </Link>
            </div>
          </footer>
        )}
      </div>
    </section>
  )
}
